import { useEffect, useState } from 'react';
import { getUsage, type UsageReport } from '../../lib/usage-api';
import { getBilling, type BillingStatus } from '../../lib/billing-api';
import styles from './admin.module.css';
import u from './Usage.module.css';

const DAY_MS = 86_400_000;
const RANGES = [
  { id: '7', label: 'Last 7 days', days: 7 },
  { id: '30', label: 'Last 30 days', days: 30 },
  { id: '90', label: 'Last 90 days', days: 90 },
  { id: 'month', label: 'This month', days: 0 },
] as const;

const fmt = new Intl.NumberFormat();
const usdWhole = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
const usdSmall = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumSignificantDigits: 2 });
// A few cents of tokens would round to "$0.00": show small amounts to two significant digits.
const usd = { format: (v: number) => (v > 0 && v < 1 ? usdSmall : usdWhole).format(v) };
const shortDay = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });

function rangeFor(id: string): { from: Date; to: Date } {
  const to = new Date();
  if (id === 'month') return { from: new Date(to.getFullYear(), to.getMonth(), 1), to };
  const days = RANGES.find((r) => r.id === id)?.days ?? 30;
  return { from: new Date(to.getTime() - days * DAY_MS), to };
}

/**
 * Usage: what the account used over a period — conversations, messages, AI
 * replies and tokens with their estimated cost, people asking for a person,
 * sign-ups, tickets — per day and per site, next to the plan's limits.
 */
export function AdminUsage() {
  const [range, setRange] = useState('30');
  const [report, setReport] = useState<UsageReport | null>(null);
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setReport(null);
    const { from, to } = rangeFor(range);
    getUsage(from, to)
      .then((r) => live && setReport(r))
      .catch(() => live && setError('Could not load usage.'));
    return () => {
      live = false;
    };
  }, [range]);
  useEffect(() => {
    getBilling()
      .then(setBilling)
      .catch(() => setBilling(null));
  }, []);

  const t = report?.totals;
  const limits = billing?.entitlements.limits;

  return (
    <section className={styles.section}>
      <p className={styles.kicker}>Usage</p>
      <div className={u.toolbar}>
        <h2 className={styles.title} style={{ margin: 0 }}>
          What your sites used
        </h2>
        <select className={styles.select} style={{ width: 'auto' }} value={range} onChange={(e) => setRange(e.target.value)} aria-label="Period">
          {RANGES.map((r) => (
            <option key={r.id} value={r.id}>
              {r.label}
            </option>
          ))}
        </select>
      </div>
      {error && <p className={styles.error} role="alert">{error}</p>}

      {billing && limits && (
        <div className={u.meters}>
          <Meter label="AI replies this billing period" used={billing.usage.aiReplies} limit={limits.aiReplyQuota} />
          <Meter label="Agent seats" used={billing.usage.seats} limit={limits.seatLimit} />
          <Meter label="Sites" used={billing.usage.sites} limit={limits.siteLimit} />
        </div>
      )}

      {!t || !report ? (
        !error && <p className={styles.hint}>Loading…</p>
      ) : (
        <>
          <div className={u.tiles}>
            <Tile label="Conversations" value={fmt.format(t.conversations)} />
            <Tile label="AI replies" value={fmt.format(t.aiReplies)} />
            <Tile
              label="Messages"
              value={fmt.format(t.messages.visitor + t.messages.agent + t.messages.ai)}
              note={`${fmt.format(t.messages.visitor)} visitor · ${fmt.format(t.messages.agent)} agent · ${fmt.format(t.messages.ai)} AI`}
            />
            <Tile
              label="AI tokens"
              value={fmt.format(t.inputTokens + t.outputTokens)}
              note={`${fmt.format(t.inputTokens)} in · ${fmt.format(t.outputTokens)} out`}
            />
            <Tile
              label="Estimated AI cost"
              value={t.aiCostUsd === null ? '—' : usd.format(t.aiCostUsd)}
              note={t.aiCostUsd === null ? 'A model in use has no price set (Admin → System → AI costs)' : 'From the model price table'}
            />
            <Tile label="Asked for a person" value={fmt.format(t.humanRequests)} />
            <Tile label="AI assists" value={fmt.format(t.aiAssists)} note="Playground and suggested replies" />
            <Tile label="Newsletter sign-ups" value={fmt.format(t.newsletterSignups)} />
            <Tile label="Tickets created" value={fmt.format(t.ticketsCreated)} />
          </div>

          <div className={u.charts}>
            <DailyBars title="Conversations per day" days={report.daily} pick={(d) => d.conversations} />
            <DailyBars title="AI replies per day" days={report.daily} pick={(d) => d.aiReplies} />
          </div>

          <p className={styles.label} style={{ margin: 'var(--inic-space-5) 0 var(--inic-space-2)' }}>
            By site
          </p>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Site</th>
                  <th className={u.num}>Conversations</th>
                  <th className={u.num}>AI replies</th>
                  <th className={u.num}>Tokens</th>
                  <th className={u.num}>AI cost</th>
                  <th className={u.num}>Asked for a person</th>
                  <th className={u.num}>Data sources</th>
                  <th className={u.num}>Open suggestions</th>
                </tr>
              </thead>
              <tbody>
                {report.sites.map((s) => (
                  <tr key={s.siteId}>
                    <td>{s.name}</td>
                    <td className={u.num}>{fmt.format(s.conversations)}</td>
                    <td className={u.num}>{fmt.format(s.aiReplies)}</td>
                    <td className={u.num}>{fmt.format(s.inputTokens + s.outputTokens)}</td>
                    <td className={u.num}>{s.aiCostUsd === null ? '—' : usd.format(s.aiCostUsd)}</td>
                    <td className={u.num}>{fmt.format(s.humanRequests)}</td>
                    <td className={u.num}>{fmt.format(s.knowledgeEntries)}</td>
                    <td className={u.num}>{fmt.format(s.openSuggestions)}</td>
                  </tr>
                ))}
                {report.sites.length === 0 && (
                  <tr>
                    <td colSpan={8} className={styles.hint}>
                      No sites yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function Tile({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className={u.tile}>
      <span className={u.tileLabel}>{label}</span>
      <span className={u.tileValue}>{value}</span>
      {note && <span className={u.tileNote}>{note}</span>}
    </div>
  );
}

function Meter({ label, used, limit }: { label: string; used: number; limit: number | null }) {
  const pct = limit ? Math.min(100, (used / limit) * 100) : 0;
  return (
    <div className={u.meter}>
      <span className={u.tileLabel}>{label}</span>
      <span className={u.meterValue}>
        {fmt.format(used)}
        <span className={u.tileNote}> of {limit === null ? 'unlimited' : fmt.format(limit)}</span>
      </span>
      {limit !== null && (
        <span
          className={u.meterTrack}
          role="meter"
          aria-valuenow={used}
          aria-valuemin={0}
          aria-valuemax={limit}
          aria-label={label}
          data-near={pct >= 90 ? 'true' : undefined}
        >
          <span className={u.meterFill} style={{ width: `${pct}%` }} />
        </span>
      )}
    </div>
  );
}

/** One series per chart (never two scales on one axis): bars on a zero
 *  baseline, the peak labelled, every bar's value on hover. */
function DailyBars({
  title,
  days,
  pick,
}: {
  title: string;
  days: UsageReport['daily'];
  pick: (d: UsageReport['daily'][number]) => number;
}) {
  const max = Math.max(1, ...days.map(pick));
  const total = days.reduce((n, d) => n + pick(d), 0);
  return (
    <figure className={u.chart}>
      <figcaption className={u.chartTitle}>
        {title}
        <span className={u.tileNote}> · peak {fmt.format(max === 1 && total === 0 ? 0 : max)}</span>
      </figcaption>
      <div className={u.bars} role="img" aria-label={`${title}: ${fmt.format(total)} in total`}>
        {days.map((d) => {
          const v = pick(d);
          const label = `${shortDay.format(new Date(`${d.day}T00:00:00Z`))}: ${fmt.format(v)}`;
          return (
            <span key={d.day} className={u.barSlot} title={label} aria-label={label}>
              <span className={u.bar} style={{ height: v ? `max(2px, ${(v / max) * 100}%)` : 0 }} />
            </span>
          );
        })}
      </div>
      {days.length > 0 && (
        <div className={u.axis}>
          <span>{shortDay.format(new Date(`${days[0]!.day}T00:00:00Z`))}</span>
          <span>{shortDay.format(new Date(`${days[days.length - 1]!.day}T00:00:00Z`))}</span>
        </div>
      )}
    </figure>
  );
}
